import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { environment } from '../../../../environments/environment';
import { FormsServiceService } from '../../../api/services/forms-service.service';
import { filter, Subscription } from 'rxjs';
import { NavigationStart, Router } from '@angular/router';
import { ModalRendererComponent } from '../../widgets/modal-renderer/modal-renderer.component';
import {
  COLORS,
  getColor,
  PRIORITY_COLORS,
  RISK_COLORS,
  STATUS_COLORS,
} from '../../../helpers/constants/colors.constant';
import { FORM_ID } from '../../../helpers/form-id.enum';

@Component({
  selector: 'app-dropdown-select',
  imports: [CommonModule, FormsModule, ReactiveFormsModule],
  templateUrl: './dropdown-select.component.html',
  styleUrl: './dropdown-select.component.css',
})
export class DropdownSelectComponent {
  @Input() formGroup!: FormGroup;
  @Input() fields: any;
  @Input() labelAbove: any;
  @Input() showTags: any;
  @Input() disableFields: boolean = false;
  @Output() valueChanged = new EventEmitter<any>();
  @ViewChild('focusInput') focusInput!: ElementRef<HTMLInputElement>;
  fieldsData: any;
  endpoint = environment.api_endpoint;
  selectedValue: any;
  @Input() autoFocused: any;
  selectedItem: any;
  selectedItems: any[] = [];
  isOpen: boolean = false;
  openedDirection: 'above' | 'below' | null = null;
  inputWithTags: string[] = [];
  openUpwards: boolean = false;
  openAboveId: any;
  openDropdownId: any;
  private routerSub!: Subscription;
  private openAnimationFrameId: number | null = null;
  @ViewChild(ModalRendererComponent)
  modalRenderer!: ModalRendererComponent;
  @ViewChild('dropdownPanel') dropdownPanel!: ElementRef;
  searchTerm: string = '';
  searchTimeout: any;
  filteredData: any[] = [];
  formId: any;
  currentPage = 1;
  limit = 20;
  isLoadingMore = false;
  hasMoreData = true;
  constructor(
    private formService: FormsServiceService,
    private elementRef: ElementRef,
    private router: Router,
    private cd: ChangeDetectorRef,
  ) {}
  ngOnInit() {
    this.getData();
    const control = this.formGroup?.get(this.fields.fieldName);
    if (control) {
      this.syncSelectedFromForm(control.value);

      control.valueChanges.subscribe((value) => {
        this.syncSelectedFromForm(value);
      });
    }
    document.addEventListener('click', this.handleClickOutside, true);
    window.addEventListener('scroll', this.handleScroll, true);
    this.routerSub = this.router.events
      .pipe(filter((event) => event instanceof NavigationStart))
      .subscribe(() => {
        this.closeDropdown();
        this.cd.detectChanges();
      });
  }

  closeOnScroll = () => {
    if (this.isOpen) {
      this.closeDropdown();
    }
  };

  handleScroll = (event: Event) => {
    if (!this.isOpen) return;

    const panel = this.dropdownPanel?.nativeElement;
    if (panel?.contains(event.target as Node)) return;

    this.closeDropdown();
  };

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    this.handleClickOutside(event);
  }

  ngOnDestroy() {
    this.closeDropdown();
    this.routerSub?.unsubscribe();
    document.removeEventListener('click', this.handleClickOutside, true);
    window.removeEventListener('scroll', this.handleScroll, true);
  }

  get control() {
    return this.formGroup.get(this.fields.fieldName);
  }

  handleClickOutside = (event: MouseEvent) => {
    if (!this.isOpen) return;

    const panel = this.dropdownPanel?.nativeElement;
    const trigger = this.focusInput?.nativeElement;

    const clickedInsideTrigger = trigger?.contains(event.target as Node);
    const clickedInsidePanel = panel?.contains(event.target as Node);

    if (clickedInsideTrigger || clickedInsidePanel) {
      return;
    }

    this.closeDropdown();
  };
  syncSelectedFromForm(value?: any): void {
    let formValue = value ?? this.formGroup?.get(this.fields.fieldName)?.value;
    if (!formValue || !this.fieldsData?.length) {
      this.selectedItem = null;
      this.selectedItems = [];
      return;
    }

    const getValue = (item: any) => String(this.getOptionValue(item)); // 🔥 normalize to string

    // 🔥 Normalize array formValue (single select case)
    if (!this.fields?.multiselect && Array.isArray(formValue) && formValue.length > 0) {
      formValue = formValue[0];
    }

    // 🔥 Normalize object formValue (single select case)
    if (typeof formValue === 'object' && formValue !== null && !Array.isArray(formValue)) {
      formValue = this.getOptionValue(formValue);
    }

    // 🟢 MULTI SELECT
    if (this.fields?.multiselect) {
      const arrVal = Array.isArray(formValue)
        ? formValue
        : typeof formValue === 'string' && formValue.includes(',')
          ? formValue.split(',').map((v: string) => v.trim())
          : [formValue];
      const normalizedValues = arrVal.map((v: any) =>
        String(this.getOptionValue(v) ?? v).toLowerCase(),
      );
      this.selectedItems = this.fieldsData?.filter((item: any) =>
        normalizedValues.includes(getValue(item).toLowerCase()),
      );
      return;
    }

    // 🔵 SINGLE SELECT
    const normalizedValue = String(formValue).toLowerCase(); // 🔥 normalize

    this.selectedItem =
      this.fieldsData?.find(
        (item: any) =>
          getValue(item).toLowerCase() === normalizedValue ||
          (item &&
            typeof item === 'object' &&
            ((item._id && String(item._id).toLowerCase() === normalizedValue) ||
              (item.id && String(item.id).toLowerCase() === normalizedValue) ||
              (item.role && String(item.role).toLowerCase() === normalizedValue) ||
              (item.name && String(item.name).toLowerCase() === normalizedValue) ||
              (item.label && String(item.label).toLowerCase() === normalizedValue) ||
              (item.value && String(item.value).toLowerCase() === normalizedValue))),
      ) || null;
  }

  ngOnChanges() {
    if (!this.autoFocused) return;

    setTimeout(() => {
      this.focusInput?.nativeElement.focus();
    });
  }
  /** Function to get the data by api or static */
  getData() {
    if (this.fields.options.isStatic === true) {
      this.fieldsData = this.fields.options.staticOptions;
      this.filterOptions();
      const control = this.formGroup?.get(this.fields.fieldName);
      if (control?.value) {
        this.syncSelectedFromForm(control.value);
      }
    } else {
      this.formId = this.fields.options.source.formID;
      this.currentPage = 1;
      this.hasMoreData = true;
      this.loadDataPage(this.currentPage);
    }
  }

  loadDataPage(page: number) {
    if (!this.formId) return;

    const api_endpoint = this.endpoint;
    const method = this.fields.options.source.method;
    let url = this.fields.options.source.apiUrl;

    const replacements = {
      formID: this.formId,
      api_endpoint: api_endpoint,
    };

    url = this.formService.replaceTemplateTokens(url, replacements);

    const headers = {
      'Content-Type': 'application/json',
    };
    const payload =
      this.formId === '6a0ed7f32db4b8fda5f347a6'
        ? {}
        : {
            page: page,
            limit: this.limit,
          };

    this.formService.loadData(method, url, headers, payload).subscribe({
      next: (res: any) => {
        const newRecords = res.data.records || [];
        if (page === 1) {
          this.fieldsData = newRecords;
        } else {
          this.fieldsData = [...(this.fieldsData || []), ...newRecords];
        }

        if (newRecords.length < this.limit) {
          this.hasMoreData = false;
        }

        this.filterOptions();
        // 🔥 IMPORTANT: sync again after data loads
        const control = this.formGroup?.get(this.fields.fieldName);
        if (control) {
          this.syncSelectedFromForm(control.value);
        }
        this.cd.detectChanges();
        this.isLoadingMore = false;
      },
      error: (err: any) => {
        if (page === 1) {
          this.fieldsData = [];
          this.filterOptions();
        }
        this.isLoadingMore = false;
      },
    });
  }

  onScroll(event: Event) {
    if (this.fields?.options?.isStatic === true) return;

    const target = event.target as HTMLElement;
    if (target.scrollHeight - target.scrollTop - target.clientHeight < 10) {
      this.loadMore();
    }
  }

  loadMore() {
    if (this.isLoadingMore || !this.hasMoreData || !this.formId) return;
    this.isLoadingMore = true;
    this.currentPage++;
    this.loadDataPage(this.currentPage);
  }

  getSelectedOptionColor(selectedValue: any): string {
    if (!selectedValue) return '#808290';

    // MULTI SELECT
    if (Array.isArray(selectedValue)) {
      if (!selectedValue.length) return '#808290';

      const selectedItems =
        this.fieldsData?.filter((item: any) =>
          selectedValue.includes(item.id),
        ) || [];

      if (selectedItems.length) {
        const item = selectedItems[0];
        return this.getColorFromAllMaps(item.label);
      }

      return '#808290';
    }

    // SINGLE SELECT
    const selected = this.fieldsData?.find(
      (item: any) => item.id === selectedValue,
    );

    if (!selected) return '#808290';

    // ✅ FIX: use same logic as multi-select
    return this.getColorFromAllMaps(selected.label);
  }

  getColorFromAllMaps(label: string): string {
    return COLORS[label?.toLowerCase()] || '#808290';
  }

  getSelectedOptions(): any[] {
    const control = this.formGroup.get(this.fields.fieldName);
    if (!control || !control.value) return [];

    const selectedValues = control.value; // array of ids

    return this.fieldsData?.filter((option: any) =>
      selectedValues.includes(this.getOptionValue(option)),
    );
  }
  get selectedOptions(): any[] {
    const control = this.formGroup.get(this.fields.fieldName);
    if (!control || !control.value) return [];

    const selectedValues = control.value;

    return this.fieldsData?.filter((option: any) =>
      selectedValues.includes(this.getOptionValue(option)),
    );
  }

  removeTag(index: number) {
    this.inputWithTags.splice(index, 1);
    this.formGroup.get(this.fields.fieldName)?.setValue(this.inputWithTags);
  }

  getSelectedDisplay(): string {
    const isLocation = this.fields?.fieldName === 'location';

    // 🟢 Multi Select
    if (this.fields?.multiselect) {
      const source = isLocation
        ? this.formGroup.get(this.fields.fieldName)?.value
        : this.selectedItems;

      if (Array.isArray(source) && source.length > 0) {
        const labels = source.map((item: any) =>
          this.formId === '6a3d163923bf07d3d695d993'
            ? item?.shortName || item?.name || item?.text || ''
            : item?.domain && item?.fullName
              ? item.domain + ' (' + item.fullName + ')'
              : item?.name || item?.text || item?.domain || item.label || '',
        );

        if (labels.length <= 3) {
          return labels.join(', ');
        }

        return labels.slice(0, 2).join(', ') + ` +${labels.length - 2}`;
      }

      return (
        this.formatPlaceholder(this.fields?.placeholder) || 'Select option'
      );
    }

    // 🔵 Single Select
    const source = isLocation
      ? this.formGroup.get(this.fields.fieldName)?.value
      : this.selectedItem;

    if (source) {
      return this.formId === '6a3d163923bf07d3d695d993'
        ? source?.shortName || source?.name || source?.text || ''
        : (source?.domain && source?.fullName
            ? source.domain + ' (' + source.fullName + ')'
            : null) ||
            source?.name ||
            source?.role ||
            source?.text ||
            source?.domain ||
            source?.label ||
            this.fields?.placeholder ||
            'Select option';
    }

    return this.formatPlaceholder(this.fields?.placeholder) || 'Select option';
  }

  getOptionValue(option: any): any {
    // Special form
    if (this.formId === '6a3d163923bf07d3d695d993') {
      return (
        option?.shortName ||
        option?.name ||
        option?.text ||
        option?.id ||
        option?._id ||
        ''
      );
    }
    if (this.formId === '6a0edc492db4b8fda5f347b7') {
      return option?.text;
    }
    return (
      option?.email ??
      option?.domain ??
      option?.id ??
      option?.value ??
      option?.label ??
      option?.role ??
      option?._id
    );
  }

  isSelected(option: any): boolean {
    const control = this.formGroup.get(this.fields.fieldName);
    if (!control?.value) return false;

    const optionId = this.getOptionValue(option);

    if (this.fields.fieldName === 'location') {
      return control.value.some(
        (v: any) => this.getOptionValue(v) === optionId,
      );
    }

    if (Array.isArray(control.value)) {
      return control.value.some((v: any) => {
        if (typeof v === 'object' && v !== null) {
          return (
            this.getOptionValue(v) === optionId ||
            (v._id && v._id === option?._id) ||
            (v.role && v.role === option?.role) ||
            (v.name && v.name === option?.name) ||
            String(this.getOptionValue(v)).toLowerCase() === String(optionId).toLowerCase()
          );
        }
        return (
          v === optionId ||
          String(v).toLowerCase() === String(optionId).toLowerCase()
        );
      });
    }

    if (typeof control.value === 'object' && control.value !== null) {
      return (
        control.value === option ||
        this.getOptionValue(control.value) === optionId ||
        (control.value._id && control.value._id === option?._id) ||
        (control.value.id && control.value.id === option?.id) ||
        (control.value.role && control.value.role === option?.role) ||
        (control.value.name && control.value.name === option?.name) ||
        String(this.getOptionValue(control.value)).toLowerCase() === String(optionId).toLowerCase()
      );
    }

    return (
      control.value === optionId ||
      control.value === option ||
      String(control.value).toLowerCase() === String(optionId).toLowerCase()
    );
  }
  hasSelection(): boolean {
    return this.fields.multiselect
      ? this.selectedItems.length > 0
      : !!this.selectedItem;
  }

  selectOption(option: any, event?: MouseEvent): void {
    event?.stopPropagation();

    const control = this.formGroup.get(this.fields.fieldName);
    if (!control) return;

    const optionId = this.getOptionValue(option);

    // 🟢 MULTI SELECT MODE
    if (this.fields?.multiselect) {
      let value = control.value || [];

      // 👉 CHANGE: handle object vs id
      const compareValue =
        this.fields.fieldName === 'location' ? option : optionId;

      const exists = value.some((v: any) =>
        this.fields.fieldName === 'location'
          ? this.getOptionValue(v) === optionId
          : v === optionId,
      );

      if (exists) {
        value = value.filter((v: any) =>
          this.fields.fieldName === 'location'
            ? this.getOptionValue(v) !== optionId
            : v !== optionId,
        );
      } else {
        value = [...value, compareValue];
      }

      // 🔥 VALUE SETTING
      if (value.length === 0) {
        control.setValue(null, { emitEvent: true });

        if (this.fields?.isRequired) {
          control.setErrors({ required: true });
        }
      } else {
        control.setValue(value, { emitEvent: true });

        // ❗ FIX: clear error when valid
        if (this.fields?.isRequired) {
          control.setErrors(null);
        }
      }

      this.isOpen = true;
    }

    // 🔵 SINGLE SELECT MODE
    else {
      this.selectedItem = option;

      const isRoleField =
        this.fields.fieldName === 'role' ||
        this.fields.fieldName === 'roles';

      const valueToSet =
        this.fields.fieldName === 'location' ||
        this.fields.fieldName === 'controls' ||
        isRoleField ||
        this.formId === '6a0edc492db4b8fda5f347b7' ||
        this.formId === FORM_ID.CONTROLS_LIST ||
        this.formId === '6a143360ca440a759a89fa08' ||
        this.fields?.formId === '6a143360ca440a759a89fa08' ||
        this.formId === FORM_ID.INVITE_USER ||
        ((this.formId === '6a143253ca440a759a89f9ff' ||
          this.fields?.formId === '6a143253ca440a759a89f9ff' ||
          this.formId === FORM_ID.ADD_USER) &&
          isRoleField)
          ? option
          : (this.formId === '6a143253ca440a759a89f9ff' ||
              this.fields?.formId === '6a143253ca440a759a89f9ff' ||
              this.formId === FORM_ID.ADD_USER) &&
              (this.fields.fieldName === 'department' ||
                this.fields.fieldName === 'departments')
            ? Array.isArray(option)
              ? option
              : [option]
            : optionId;

      control.setValue(valueToSet, { emitEvent: true });

      // ✅ Handle required dynamically
      if (this.fields?.required && !valueToSet) {
        control.setErrors({ required: true });
      } else {
        control.setErrors(null);
      }

      this.closeDropdown();
    }

    // ✅ Ensure validation updates properly
    control.markAsDirty();
    control.markAsTouched();
    control.updateValueAndValidity({ emitEvent: true });

    this.formGroup.markAsDirty();
    this.formGroup.updateValueAndValidity();
  }

  toggleDropdown(event: MouseEvent) {
    if (
      this.disableFields ||
      this.formGroup.get(this.fields.fieldName)?.disabled
    ) {
      return;
    }
    event.stopPropagation();

    if (this.isOpen) {
      this.closeDropdown();
    } else {
      this.openDropdown();
    }
  }

  openDropdown() {
    const panel = this.dropdownPanel?.nativeElement;
    if (!panel) return;

    this.isOpen = true;

    if (this.openAnimationFrameId !== null) {
      cancelAnimationFrame(this.openAnimationFrameId);
    }

    this.openAnimationFrameId = requestAnimationFrame(() => {
      this.openAnimationFrameId = null;
      if (!this.isOpen) return;

      if (panel.parentNode !== document.body) {
        document.body.appendChild(panel);
      }

      panel.style.visibility = 'hidden';
      this.openedDirection = null;
      this.positionDropdown();
      panel.style.visibility = 'visible';
    });
  }

  positionDropdown() {
    const panel = this.dropdownPanel?.nativeElement;
    if (!panel || !this.isOpen) return;

    this.cd.detectChanges();

    const trigger =
      this.elementRef.nativeElement.querySelector('.dropdown-selected');
    if (!trigger) return;

    const rect = trigger.getBoundingClientRect();

    // Make visible for measurement
    panel.style.display = 'block';
    panel.style.position = 'absolute';

    const panelHeight = panel.offsetHeight;

    // 🔥 Get closest scrollable parent
    const scrollParent = this.getScrollParent(trigger);

    let parentRect;
    if (scrollParent === document.body) {
      parentRect = {
        top: 0,
        bottom: window.innerHeight,
      };
    } else {
      parentRect = scrollParent.getBoundingClientRect();
    }

    const spaceBelow = parentRect.bottom - rect.bottom;
    const spaceAbove = rect.top - parentRect.top;

    panel.style.left = rect.left + window.scrollX + 'px';
    panel.style.width = rect.width + 'px';

    // Determine direction once when opening or reset
    if (!this.openedDirection) {
      const expectedHeight = 200; // Max height of dropdown panel
      if (spaceBelow < expectedHeight && spaceAbove > spaceBelow) {
        this.openedDirection = 'above';
      } else {
        this.openedDirection = 'below';
      }
    }

    // Position using the locked direction
    if (this.openedDirection === 'above') {
      panel.style.top = rect.top + window.scrollY - panelHeight - 8 + 'px';
    } else {
      panel.style.top = rect.bottom + window.scrollY + 'px';
    }
  }

  private getScrollParent(element: HTMLElement): HTMLElement {
    let parent = element.parentElement;

    while (parent) {
      const style = getComputedStyle(parent);
      const overflowY = style.overflowY;

      if (overflowY === 'auto' || overflowY === 'scroll') {
        return parent;
      }

      parent = parent.parentElement;
    }

    return document.body;
  }

  closeDropdown() {
    this.isOpen = false;
    this.openedDirection = null;
    this.searchTerm = '';
    this.filterOptions();

    if (this.openAnimationFrameId !== null) {
      cancelAnimationFrame(this.openAnimationFrameId);
      this.openAnimationFrameId = null;
    }

    const panel = this.dropdownPanel?.nativeElement;
    if (!panel) return;

    panel.style.display = 'none';
    panel.style.visibility = '';

    if (panel.parentNode === document.body) {
      document.body.removeChild(panel);
    }
  }

  truncateText(value: string, limit: number = 20): string {
    if (!value) return '';
    return value.length > limit ? value.slice(0, limit) + '...' : value;
  }

  getOptionText(option: any): string {
    if (!option) return '';
    if (option.text) return option.text;
    if (option.domain) {
      return option.fullName
        ? `${option.domain} (${option.fullName})`
        : option.domain;
    }
    return option.name || option.label || '';
  }

  @HostListener('document:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent) {
    if (!this.isOpen) return;

    // Don't capture keyboard search if user is typing in an input, textarea, or contenteditable element
    const target = event.target as HTMLElement;
    if (
      target &&
      (target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable)
    ) {
      return;
    }

    // Ignore modifier combinations
    if (event.ctrlKey || event.altKey || event.metaKey) return;

    const key = event.key;

    // Handle backspace
    if (key === 'Backspace') {
      this.searchTerm = this.searchTerm.slice(0, -1);
    }
    // Only allow single characters (excluding Escape since it has its own handler)
    else if (key.length === 1 && key !== 'Escape') {
      this.searchTerm += key.toLowerCase();
    } else {
      return;
    }

    // Prevent default browser action (like page scrolling on spacebar)
    event.preventDefault();

    clearTimeout(this.searchTimeout);

    this.searchTimeout = setTimeout(() => {
      this.searchTerm = '';
    }, 800);

    this.filterOptions();
  }

  filterOptions() {
    if (!this.searchTerm) {
      this.filteredData = [...(this.fieldsData || [])];
    } else {
      const term = this.searchTerm.toLowerCase();
      this.filteredData = (this.fieldsData || []).filter((option: any) => {
        const text = this.getOptionText(option);
        return text.toLowerCase().includes(term);
      });
    }

    // Sort the list alphabetically and show
    this.filteredData.sort((a: any, b: any) => {
      const textA = this.getOptionText(a).toLowerCase();
      const textB = this.getOptionText(b).toLowerCase();
      return textA.localeCompare(textB);
    });

    // Reposition dropdown to handle height changes dynamically
    setTimeout(() => {
      this.positionDropdown();
    });
  }

  formatDisplayName(value: string): string {
    if (!value) return '';

    const uppercaseWords = [
      'api',
      'url',
      'id',
      'ip',
      'dns',
      'sso',
      'saml',
      'oauth',
      'tls',
      'ssl',
    ];
    const originalWords = value.split(' ');

    return value
      .toLowerCase()
      .split(' ')
      .map((word, index) => {
        const originalWord = originalWords[index];
        if (
          originalWord &&
          originalWord === originalWord.toUpperCase() &&
          isNaN(Number(originalWord))
        ) {
          return originalWord;
        }
        const cleanWord = word.replace(/[^a-z0-9]/g, '');
        if (uppercaseWords.includes(cleanWord)) {
          return word.toUpperCase();
        }
        return word.charAt(0).toUpperCase() + word.slice(1);
      })
      .join(' ');
  }

  @HostListener('document:keydown.escape')
  onEscapeKey() {
    if (this.isOpen) {
      this.closeDropdown();
    }
  }
  formatPlaceholder(text: string): string {
    if (!text) return '';
    return text.replace(/^--\s*|\s*--$/g, '').trim();
  }

  getAsciiArray(selectedId: string): string[] {
    const options = this.fields.options.staticOptions;
    const currentIndex = options.findIndex((o: any) => o.id === selectedId);

    return options.map((_: any, i: number) =>
      i <= currentIndex ? '\u25CF' : '\u25CB',
    );
  }

  // Visual helper methods
  getInitials(name: string): string {
    if (!name) return '?';
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return parts[0][0].toUpperCase();
  }

  getAvatarColor(name: string): string {
    if (!name) return '#808290';
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    // Beautiful selection of colors suitable for both light and dark theme
    const colors = [
      '#0052CC', // Blue
      '#00875A', // Green
      '#DE350B', // Red
      '#00B8D9', // Teal
      '#6554C0', // Purple
      '#FF5630', // Orange
      '#36B37E', // Light Green
      '#FFAB00', // Yellow
    ];
    const index = Math.abs(hash) % colors.length;
    return colors[index];
  }

  isString(val: any): boolean {
    return typeof val === 'string';
  }

  isArray(val: any): boolean {
    return Array.isArray(val);
  }

  hasValidPhoto(photo: any): boolean {
    if (!photo) return false;
    if (typeof photo !== 'string') return false;
    const trimmed = photo.trim();
    return (
      trimmed !== '' &&
      trimmed !== 'null' &&
      trimmed !== 'undefined' &&
      trimmed !== 'none'
    );
  }

  handleImageError(event: Event) {
    const imgElement = event.target as HTMLImageElement;
    imgElement.src = './assets/media/icons/default-user-icon.png';
  }
}
